import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { companyApi, type EntityLabels } from '@l2u/web-shared';
import { VENDOR_ACTION_STALE_MS } from './useVendorActionCount.ts';

/**
 * The company sidebar's badges for Training, Drivers, Assets, Corrective
 * actions, File notes and Library, from ONE request: GET /api/me/nav-counts
 * computes them in parallel server-side (apps/api/src/lib/companyNavCounts.ts
 * holds the exact definition of each). Not four client-side list fetches — every authed
 * request pays the fixed Supabase round-trip cost, and the shell would have
 * paid it four times on every load to draw four small numbers.
 */
export type CompanyNavCounts = {
  training: number;
  drivers: number;
  assets: number;
  corrective_actions: number;
  /** Library documents the company account has not opened since they changed. */
  library: number;
  /**
   * Published file notes about the company, its drivers or its vehicles with
   * something it has not read. Absent from an api before 0085.
   */
  file_notes?: number;
};

/**
 * Deliberately NOT under the ['me'] prefix. The questionnaire wizard
 * invalidates ['me'] after every step save, and none of these four counts can
 * move on a company-questionnaire save — under that prefix each "Save &
 * continue" would have paid for a nav-counts refetch too. The pages that CAN
 * move a count invalidate this key themselves (invalidateCompanyNavCounts).
 */
export const COMPANY_NAV_COUNTS_KEY = ['company-nav-counts'] as const;

export function companyNavCountsQueryOptions() {
  return {
    queryKey: COMPANY_NAV_COUNTS_KEY,
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      companyApi.get<{ counts: CompanyNavCounts }>('/api/me/nav-counts', { signal }),
    staleTime: VENDOR_ACTION_STALE_MS,
  };
}

export function invalidateCompanyNavCounts(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: COMPANY_NAV_COUNTS_KEY });
}

/**
 * Refresh the nav counts whenever `value` changes — for a page whose own data
 * is the signal that a count may have moved (the Training list's pending
 * total, an asset's assetNavCountSignal) but which has no single
 * mutation callback to hang the invalidation on.
 *
 * `undefined` means "not loaded yet", and the change AWAY from it is the
 * page's first load, not a mutation — so it is recorded without a refetch
 * (opening a page must not cost a nav-counts request). Pass `enabled: false`
 * to make it inert. (A page shared by both portals — the Training tab — calls
 * useInvalidateOnChange with its own portal's key instead.)
 */
export function useInvalidateNavCountsOnChange(value: unknown, enabled = true): void {
  useInvalidateOnChange(value, enabled ? COMPANY_NAV_COUNTS_KEY : null);
}

/**
 * The general form: refetch `queryKey` (exactly that query, not its prefix)
 * whenever `value` changes between two loaded values; inert for a null key.
 * The questionnaire wizard uses it to keep the SERVER's Questionnaire badge
 * (GET .../action-count) in step with the count its own lists now show — a
 * submit, a resubmit or a reply moves it.
 */
export function useInvalidateOnChange(value: unknown, queryKey: QueryKey | null): void {
  const qc = useQueryClient();
  const previous = useRef(value);
  useEffect(() => {
    const before = previous.current;
    previous.current = value;
    if (shouldRefreshNavCounts(before, value) && queryKey) {
      void qc.invalidateQueries({ queryKey, exact: true });
    }
    // queryKey by its content, not its identity: callers pass inline arrays.
  }, [value, qc, JSON.stringify(queryKey)]);
}

/** The hook's rule, pure for the tests: a change between two LOADED values. */
export function shouldRefreshNavCounts(before: unknown, after: unknown): boolean {
  return before !== undefined && after !== undefined && !Object.is(before, after);
}

/**
 * An asset page's value for useInvalidateNavCountsOnChange: exactly the two
 * inputs the Assets badge reads for this asset — whether it has been
 * submitted, and how many of its answers are sent back. Anything else a
 * refetch brings (a saved draft answer, a newer updated_at) leaves it equal,
 * so it costs no nav-counts request. Undefined until BOTH have loaded.
 */
export function assetNavCountSignal(
  asset: { submitted_at: string | null } | undefined,
  submissions: ReadonlyArray<{ status: string }> | undefined,
): string | undefined {
  if (!asset || !submissions) return undefined;
  const rejected = submissions.reduce((n, s) => (s.status === 'rejected' ? n + 1 : n), 0);
  return `${asset.submitted_at ?? ''}|${rejected}`;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/** The Library badge's screen-reader sentence, shared with the driver tabs. */
export function libraryUnreadSrText(n: number): string {
  return `${n} new ${plural(n, 'document', 'documents')} to read`;
}

/** The File notes badge's screen-reader sentence, as the driver tabs word it. */
export function fileNotesUnreadSrText(n: number): string {
  return `${n} unread file ${plural(n, 'note', 'notes')}`;
}

/**
 * The six routes' badges, keyed by nav `to`, each with its own screen-reader
 * sentence (the drivers one in the tenant's own noun). Undefined counts —
 * still loading, or the request failed — badge nothing rather than guess.
 */
export function companyNavCountBadges(
  counts: CompanyNavCounts | undefined,
  routes: {
    trainingTo: string;
    driversTo: string;
    assetsTo: string;
    correctiveTo: string;
    libraryTo: string;
    fileNotesTo: string;
  },
  labels: Pick<EntityLabels, 'driver' | 'drivers'>,
): Map<string, { count: number; srText: string }> {
  const out = new Map<string, { count: number; srText: string }>();
  if (!counts) return out;
  const t = counts.training;
  out.set(routes.trainingTo, {
    count: t,
    srText: `${t} training ${plural(t, 'module', 'modules')} to complete`,
  });
  const d = counts.drivers;
  out.set(routes.driversTo, {
    count: d,
    srText: `${d} ${plural(d, labels.driver, labels.drivers).toLowerCase()} ${plural(d, 'needs', 'need')} your attention`,
  });
  const a = counts.assets;
  out.set(routes.assetsTo, {
    count: a,
    srText: `${a} ${plural(a, 'asset needs', 'assets need')} your answers`,
  });
  const c = counts.corrective_actions;
  out.set(routes.correctiveTo, {
    count: c,
    srText: `${c} corrective ${plural(c, 'action', 'actions')} awaiting your response`,
  });
  // `?? 0`: an api from before 0073 sends no `library` key.
  const l = counts.library ?? 0;
  out.set(routes.libraryTo, { count: l, srText: libraryUnreadSrText(l) });
  const f = counts.file_notes ?? 0;
  out.set(routes.fileNotesTo, { count: f, srText: fileNotesUnreadSrText(f) });
  return out;
}
